import React, { useCallback, useMemo, useRef, useState } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { NavLink, useHistory, useParams } from "react-router-dom";

import useClientDashboard from "@fartherfinance/frontend/api/Dashboard/hooks/useClientDashboard";
import useGetPortfolioPlanById from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlanById";
import {
  AdvisorId,
  ClientId,
  PortfolioPlanId,
} from "@fartherfinance/frontend/api/Types";

import { InvestmentsPath } from "@src/config/routing/RouterPaths";
import PortfolioHoldingsConstructionTable from "@src/multiCustodian/components/Client/Portfolio/v2/PortfolioAnalysis/Tables/HoldingsConstructionTable/Table";
import ModelPickerModal from "@src/multiCustodian/components/PortfolioPlanV2/ModelPickerModal/ModelPickerModal";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import LogoWordmark from "@src/multiCustodian/theme/LogoWordmark";
import LinkButton from "@src/sharedComponents/LinkButton/LinkButton";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import PageHero from "@src/sharedComponents/PageHero/PageHero";
import ThreeDotsPopoverMenu from "@src/sharedComponents/Popover/ThreeDotsPopoverMenu/Menu";
import MenuItem from "@src/sharedComponents/Popover/ThreeDotsPopoverMenu/MenuItem";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import Text from "@src/sharedComponents/Typography/Text";

import PortfolioDetailsSideBar from "./SideBar/PortfolioDetailsSideBar";

import styles from "./PickPortfolio.module.css";

const PickPortfolio: React.FC = () => {
  const { clientId, portfolioPlanId } = useParams<{
    clientId: ClientId;
    portfolioPlanId: PortfolioPlanId;
  }>();

  const anchorRef = useRef<HTMLDivElement>(null);
  const [showPopoverMenu, setShowPopoverMenu] = useState<boolean>(false);

  const [showModelPickerModal, setShowModelPickerModal] =
    useState<boolean>(false);

  const history = useHistory();

  const auth = useRequestAuth();

  const dashboard = useClientDashboard(clientId, auth);
  const advisorId = useMemo(() => {
    if (dashboard.data?.advisor.advisorId) {
      return AdvisorId.parse(dashboard.data.advisor.advisorId);
    }
    return undefined;
  }, [dashboard.data]);

  const portfolioPlanResp = useGetPortfolioPlanById(portfolioPlanId, {}, auth);

  const onTrackEvent = () => {
    trackEvent({
      name: "Client Click Farther-logo",
    });
  };

  const onModelSelect = useCallback(
    (portfolioPlanId: PortfolioPlanId) => {
      history.push(
        `/Client/${clientId}/${InvestmentsPath}/PortfoliosV2/Pick/${portfolioPlanId}`
      );
    },
    [clientId, history]
  );

  if (portfolioPlanResp.hasError) {
    return <div>Error</div>;
  }

  const onClickPopoverMenuItem = () => {
    setShowModelPickerModal(true);
    setShowPopoverMenu(false);
  };

  return (
    <div className={styles.container}>
      <PageHero
        className={styles.topBar}
        start={
          <div className={styles.flexRow}>
            <div className={styles.farthermark}>
              <NavLink
                to={`/Client/${clientId}/Dashboard`}
                onClick={onTrackEvent}
              >
                <LogoWordmark className={styles.fartherLogo} />
              </NavLink>
            </div>

            <Text variant="body" color={"textSubtle"}>
              Model Portfolios
            </Text>

            <ChevronRightIcon className={styles.chevronRightIconDivider} />

            <Text variant="body" color={"text"}>
              Pick a model portfolio
            </Text>
          </div>
        }
      />

      <PageHero
        className={styles.hero}
        start={
          <div className={styles.flexRow}>
            <ArrowBackIcon
              className={styles.arrowIcon}
              onClick={() =>
                history.push(`/Client/${clientId}/Investments/PortfoliosV2`)
              }
            />

            <Text className={styles.marginRight} variant="title1">
              {portfolioPlanResp.data !== undefined ? (
                `${portfolioPlanResp.data.data.plan.name}`
              ) : (
                <Skeleton width={200} />
              )}
            </Text>

            <ThreeDotsPopoverMenu
              anchorRef={anchorRef}
              isOpen={showPopoverMenu}
              onOpen={() => setShowPopoverMenu(true)}
              onClose={() => setShowPopoverMenu(false)}
              anchorOriginHorizontal={"left"}
              transformOriginHorizontal={"left"}
            >
              <MenuItem onClick={onClickPopoverMenuItem}>
                Pick a different model
              </MenuItem>
            </ThreeDotsPopoverMenu>
          </div>
        }
        end={
          <div className={styles.flexRow}>
            <LinkButton
              text="Cancel"
              variant="text"
              buttonType="primary"
              to={`/Client/${clientId}/Investments/PortfoliosV2`}
            />

            <div className={styles.continueButton}>
              <LinkButton
                text={"Continue"}
                variant="contained"
                buttonType="primary"
                to={`/Client/${clientId}/Investments/PortfoliosV2/${portfolioPlanId}/Accounts`}
                sx={{ width: "200px" }}
              />
            </div>
          </div>
        }
      />

      {portfolioPlanResp.isLoading ? <LogoLoadingStill onTop={true} /> : null}

      <ModelPickerModal
        isOpen={showModelPickerModal}
        closeModal={() => setShowModelPickerModal(false)}
        onSubmit={onModelSelect}
        clientId={clientId}
        advisorId={advisorId}
      />
      <div className={styles.body}>
        <div className={styles.contentLeft}>
          <div className={styles.space} />
          <PortfolioHoldingsConstructionTable
            portfolioPlanId={portfolioPlanId}
          />
          <div className={styles.space} />
        </div>
        <div className={styles.contentRight}>
          <PortfolioDetailsSideBar portfolioPlanId={portfolioPlanId} />
        </div>
      </div>
    </div>
  );
};

export default PickPortfolio;
